import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import {
  getUserByUsername,
  deleteUser,
  resetPassword,
  updateBiography,
  updateEmail,
  updateVisibility,
} from '../services/userService';
import { ExtendedSafeDatabaseUser } from '../types/types';
import useUserContext from './useUserContext';
import useLoginContext from './useLoginContext';
import useThemeToggle from './useThemeToggle';

/**
 * A custom hook to encapsulate all logic/state for the ProfileSettings component.
 */
const useProfileSettings = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useUserContext();
  const { user: loginUser, setUser: setLoginUser } = useLoginContext();
  const { handleToggleTheme, themeRefreshHandler } = useThemeToggle();

  // Local state
  const [userData, setUserData] = useState<ExtendedSafeDatabaseUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [editEmailMode, setEditEmailMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editBioMode, setEditBioMode] = useState(false);
  const [newBio, setNewBio] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // For delete-user confirmation modal
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const [showPassword, setShowPassword] = useState(false);

  const canEditProfile =
    currentUser.username && userData?.username ? currentUser.username === userData.username : false;

  const isGoogleUser = userData?.username && userData.username.includes('@gmail.com');
  const isGithubUser = userData?.username && userData.username.startsWith('github_');
  const isProviderUser = isGoogleUser || isGithubUser;

  const getProviderName = () => {
    if (isGoogleUser) return 'Google';
    if (isGithubUser) return 'GitHub';
    return '';
  };

  useEffect(() => {
    setNewPassword('');
    setConfirmNewPassword('');
    setNewEmail('');
    setEditEmailMode(false);
    setEditBioMode(false);
    setNewBio('');
    setSuccessMessage(null);
    setErrorMessage(null);
  }, [username]);

  // Load user data
  useEffect(() => {
    if (!username) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getUserByUsername(username);
        if (!data) {
          // User not found, handle the error appropriately
          setErrorMessage('User not found');
          return;
        }
        setUserData(data);
        if (data.email) {
          setNewEmail(data.email);
        }
        if (data.biography) {
          setNewBio(data.biography);
        }
      } catch (error) {
        setErrorMessage('Failed to load user data');
        setUserData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [username]);

  /**
   * Toggles the visibility of the password fields.
   */
  const togglePasswordVisibility = () => {
    setShowPassword(prevState => !prevState);
  };

  /**
   * Validate the password fields before attempting to reset.
   */
  const validatePasswords = () => {
    if (newPassword.trim() === '' || confirmNewPassword.trim() === '') {
      setErrorMessage('Please enter and confirm your new password.');
      return false;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorMessage('Passwords do not match.');
      return false;
    }
    return true;
  };

  /**
   * Validate the email field
   */
  const validateEmail = () => {
    if (newEmail.trim() === '') {
      setErrorMessage('Please enter an email address.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setErrorMessage('Please enter a valid email address.');
      return false;
    }

    return true;
  };

  /**
   * Handler for resetting the password
   */
  const handleResetPassword = async () => {
    if (!username) return;
    if (isProviderUser) {
      setErrorMessage(
        "Provider-authenticated users cannot change their password here. Please use the provider's website to manage your account.",
      );
      return;
    }
    if (!validatePasswords()) {
      return;
    }
    try {
      await resetPassword(username, newPassword);
      setSuccessMessage('Password reset successful!');
      setErrorMessage(null);
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      setErrorMessage('Failed to reset password.');
      setSuccessMessage(null);
    }
  };

  /**
   * Handler for updating the biography
   */
  const handleUpdateBiography = async () => {
    if (!username) return;
    try {
      // Await the async call to update the biography
      const updatedUser = await updateBiography(username, newBio);

      // Ensure state updates occur sequentially after the API call completes
      await new Promise(resolve => {
        setUserData(updatedUser);
        setEditBioMode(false);
        resolve(null);
      });

      setSuccessMessage('Biography updated!');
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage('Failed to update biography.');
      setSuccessMessage(null);
    }
  };

  /**
   * Handler for updating the email
   */
  const handleUpdateEmail = async () => {
    if (!username) return;
    if (!validateEmail()) {
      return;
    }

    try {
      const updatedUser = await updateEmail(username, newEmail);

      await new Promise(resolve => {
        setUserData(updatedUser);
        setEditEmailMode(false);
        resolve(null);
      });

      // Update the login context if the updated user is the currently logged in user
      if (loginUser && loginUser.username === username) {
        setLoginUser(updatedUser);
      }

      setSuccessMessage('Email updated successfully!');
      setErrorMessage(null);
    } catch (error: unknown) {
      // Check if it's a 404 error from Axios
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 404) {
          setErrorMessage(
            'The server endpoint for updating email is not available. Please try again later.',
          );
        } else {
          setErrorMessage(`Failed to update email: ${axiosError.message || 'Unknown error'}`);
        }
      } else {
        setErrorMessage(`Failed to update email: ${(error as Error)?.message || 'Unknown error'}`);
      }
      setSuccessMessage(null);
    }
  };

  /**
   * Handler for deleting the user (triggers confirmation modal)
   */
  const handleDeleteUser = () => {
    if (!username) return;
    if (isProviderUser) {
      setErrorMessage(
        "Provider-authenticated users cannot delete their account here. Please use the provider's website to manage your account.",
      );
      return;
    }

    setShowConfirmation(true);
    setPendingAction(() => async () => {
      try {
        await deleteUser(username);
        setSuccessMessage(`User "${username}" deleted successfully.`);
        setErrorMessage(null);
        navigate('/');
      } catch (error) {
        setErrorMessage('Failed to delete user.');
        setSuccessMessage(null);
      } finally {
        setShowConfirmation(false);
      }
    });
  };

  /**
   * Toggles the visibility of the user's profile (public/private)
   */
  const handleToggleVisibility = async () => {
    if (!username) return;
    try {
      // Determine the new visibility state
      const currentVisibility = userData?.visibility === 'public';
      const newVisibility = currentVisibility ? 'private' : 'public';

      // Call the API to update visibility
      const updatedUser = await updateVisibility(username, newVisibility);

      // Update local state with the response from the API
      setUserData(updatedUser);

      // Show success message
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage('Failed to update visibility.');
      setSuccessMessage(null);
    }
  };

  return {
    userData,
    loading,
    canEditProfile,
    isProviderUser,
    getProviderName,
    newPassword,
    setNewPassword,
    confirmNewPassword,
    setConfirmNewPassword,
    handleResetPassword,
    showPassword,
    setShowPassword,
    newEmail,
    setNewEmail,
    editEmailMode,
    setEditEmailMode,
    handleUpdateEmail,
    newBio,
    setNewBio,
    editBioMode,
    setEditBioMode,
    handleUpdateBiography,
    handleToggleVisibility,
    successMessage,
    setSuccessMessage,
    errorMessage,
    setErrorMessage,
    handleDeleteUser,
    showConfirmation,
    setShowConfirmation,
    pendingAction,
    setPendingAction,
    themeRefreshHandler,
    handleToggleTheme,
    togglePasswordVisibility,
  };
};

export default useProfileSettings;
